import React from 'react';
import { fmt } from './shared';
import { computeWordDiff, getChangedFields } from './messageVersions';

export function WordDiff({ current = '', previous = '' }) {
  return computeWordDiff(current, previous).map((part, index) => (
    <span
      key={`${part.status}-${index}`}
      className={part.status === 'added'
        ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
        : part.status === 'removed'
          ? 'bg-red-100 text-red-700 line-through dark:bg-red-900/40 dark:text-red-300'
          : undefined}
    >
      {part.value}{' '}
    </span>
  ));
}

export function ListDiff({ current = [], previous = [] }) {
  const currentSet = new Set(current);
  const previousSet = new Set(previous);
  const entries = [
    ...current.filter(item => !previousSet.has(item)).map(item => ({ item, status: 'added' })),
    ...previous.filter(item => !currentSet.has(item)).map(item => ({ item, status: 'removed' })),
    ...current.filter(item => previousSet.has(item)).map(item => ({ item, status: 'unchanged' })),
  ];

  return entries.map(({ item, status }) => (
    <span
      key={`${status}-${item}`}
      className={`text-sm border px-3 py-1 rounded-full ${status === 'added'
        ? 'bg-green-50 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700'
        : status === 'removed'
          ? 'bg-red-50 text-red-700 border-red-300 line-through dark:bg-red-900/30 dark:text-red-300 dark:border-red-700'
          : 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700'}`}
    >
      {item}
    </span>
  ));
}

export function VersionTimeline({ history, selectedVersion, onSelect }) {
  if (history.length < 2) return null;
  const latest = history[history.length - 1];

  return (
    <aside className="lg:sticky lg:top-20 lg:self-start" aria-label="Version history">
      <h2 className="text-xs font-semibold uppercase text-gray-400 dark:text-gray-500 mb-4">Version History</h2>
      <ol className="border-l border-gray-200 dark:border-gray-700 ml-1.5 space-y-6">
        {[...history].reverse().map((entry, index) => {
          const previous = history.find(item => item._version === entry._version - 1);
          const changed = getChangedFields(entry, previous);
          const isLatest = entry._version === latest._version;
          const isSelected = entry._version === selectedVersion;
          return (
            <li key={entry._version} className="relative pl-5">
              <span className={`absolute -left-1.5 top-1 w-3 h-3 rounded-full border-2 border-white dark:border-gray-950 ${isLatest ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'}`} />
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                  {fmt(entry._versionCreatedAt ?? entry.lastModifiedDateTime)}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${isLatest ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'}`}>
                  {isLatest ? `Latest · v${entry._version}` : `v${entry._version}`}
                </span>
              </div>
              <p className="text-xs text-gray-400 dark:text-gray-500 leading-relaxed mb-1.5">
                {index === history.length - 1 ? 'Initial version' : `Changed: ${changed.join(', ') || 'No tracked fields'}`}
              </p>
              {!isSelected && (
                <button type="button" onClick={() => onSelect(entry._version)} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
                  View this version
                </button>
              )}
              {isSelected && !isLatest && (
                <button type="button" onClick={() => onSelect(latest._version)} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
                  View latest
                </button>
              )}
            </li>
          );
        })}
      </ol>
    </aside>
  );
}

export function CompareToggle({ previousVersion, checked, onChange }) {
  if (!previousVersion) return null;
  return (
    <label className="flex items-center gap-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 px-4 py-3 mb-6 cursor-pointer">
      <span className="relative inline-flex shrink-0">
        <input type="checkbox" className="peer sr-only" checked={checked} onChange={event => onChange(event.target.checked)} />
        <span className="w-9 h-5 rounded-full bg-gray-300 peer-checked:bg-blue-600 dark:bg-gray-700 transition-colors" />
        <span className="absolute left-0.5 top-0.5 w-4 h-4 rounded-full bg-white peer-checked:translate-x-4 transition-transform" />
      </span>
      <span className="text-sm font-medium text-gray-800 dark:text-gray-200">Compare with previous version (v{previousVersion})</span>
    </label>
  );
}